// ネクストエンジン原価取得ジョブの状態をポーリングするフック。
// 非同期ジョブ(全件取得は数分かかる)を 202 で受けた後、GET /api/nextengine/jobs/:jobId を
// 一定間隔で叩き、running の途中経過(result_json.progress)や最終結果を反映する。
// 間隔は最初 3 秒、60 秒経過後は 5 秒に緩め、上限 20 分で打ち切る。

import { useCallback, useEffect, useRef, useState } from "react";
import { apiGetJson } from "../utils/apiClient";

export type NeJobStatus =
    | "queued"
    | "running"
    | "succeeded"
    | "partial"
    | "failed"
    | "cancelled";

export type NeJob = {
    jobId: string;
    status: NeJobStatus;
    totalCount: number;
    succeededCount: number;
    failedCount: number;
    errorSummary: string | null;
    resultJson: any | null;
    updatedAt: string;
    finishedAt: string | null;
};

// apiGetJson は 401 で Error("UNAUTHORIZED")、その他の非 2xx で
// Error("<status> <path>: <body>") を投げる。ポーリングを打ち切るべき致命的な
// ステータス(401/403/404)を判定し、利用者向けの案内文を返す(該当なしは null)。
function fatalPollingMessage(err: unknown): string | null {
    const message = err instanceof Error ? err.message : String(err ?? "");
    if (message === "UNAUTHORIZED" || /^401\b/.test(message)) {
        return "ログインの有効期限が切れた可能性があります。お手数ですが、再度ログインしてからお試しください。";
    }
    if (/^403\b/.test(message)) {
        return "この操作の権限がありません。連携の設定をご確認ください。";
    }
    if (/^404\b/.test(message)) {
        return "取得ジョブが見つかりませんでした。お手数ですが、もう一度「原価を取得」からやり直してください。";
    }
    return null;
}

const FINISHED = new Set<NeJobStatus>(["succeeded", "partial", "failed", "cancelled"]);
const FAST_INTERVAL_MS = 3000;
const SLOW_INTERVAL_MS = 5000;
const SLOW_AFTER_MS = 60_000;
const MAX_DURATION_MS = 20 * 60 * 1000;

export function useNeJobPolling() {
    const [job, setJob] = useState<NeJob | null>(null);
    const [polling, setPolling] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const startedAtRef = useRef<number>(0);
    const activeRef = useRef(false);
    const jobIdRef = useRef<string | null>(null);

    const clearTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const stop = useCallback(() => {
        activeRef.current = false;
        clearTimer();
        setPolling(false);
    }, []);

    const tick = useCallback(async () => {
        if (!activeRef.current) return;
        const jobId = jobIdRef.current;
        if (!jobId) return;

        try {
            const res = await apiGetJson<{ ok: boolean; job?: NeJob }>(
                `/api/nextengine/jobs/${encodeURIComponent(jobId)}`,
            );
            if (!activeRef.current) return;
            if (res?.job) {
                setJob(res.job);
                if (FINISHED.has(res.job.status)) {
                    stop();
                    return;
                }
            }
        } catch (err) {
            if (!activeRef.current) return;
            // 401/403/404 は一時的エラーではない(再ログインや連携やり直しが必要)。
            // 20分スピンし続けず、具体的な案内を出して即停止する。
            const fatal = fatalPollingMessage(err);
            if (fatal) {
                setError(fatal);
                stop();
                return;
            }
            // それ以外(ネットワーク一時障害・5xx 等)は握りつぶして継続(次のtickで回復)。上限で打ち切る。
        }

        const elapsed = Date.now() - startedAtRef.current;
        if (elapsed >= MAX_DURATION_MS) {
            setError("取得の完了確認がタイムアウトしました。時間をおいて状態を再確認してください。");
            stop();
            return;
        }
        const interval = elapsed >= SLOW_AFTER_MS ? SLOW_INTERVAL_MS : FAST_INTERVAL_MS;
        timerRef.current = setTimeout(() => void tick(), interval);
    }, [stop]);

    const startPolling = useCallback(
        (jobId: string) => {
            clearTimer();
            jobIdRef.current = jobId;
            activeRef.current = true;
            startedAtRef.current = Date.now();
            setError(null);
            setPolling(true);
            void tick();
        },
        [tick],
    );

    // アンマウント時にタイマーを止める。
    useEffect(() => () => stop(), [stop]);

    return { job, polling, error, startPolling, stop, setJob };
}
